/**
 * @module  public/js/focusmode/cta-instance.js
 * @summary Utilities for generating and caching a per-task CTA instance ID.
 *
 * The CTA instance ID links primary and secondary actions for the same task.
 * IDs persist for the duration of the browser session using `sessionStorage`.
 */
/**
 * Generate an RFC 4122 v4 UUID using available crypto APIs.
 * Prefers `crypto.randomUUID`; falls back to `crypto.getRandomValues` when
 * necessary.
 * @returns {string} v4 UUID
 */
function generateUuid() {
  if (typeof crypto?.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto?.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0'));
    return (
      hex.slice(0, 4).join('') +
      '-' +
      hex.slice(4, 6).join('') +
      '-' +
      hex.slice(6, 8).join('') +
      '-' +
      hex.slice(8, 10).join('') +
      '-' +
      hex.slice(10, 16).join('')
    );
  }
  // Last resort non-crypto fallback.
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

export class CtaInstance {
  /**
   * Ensure a CTA instance ID exists for the given task.
   * @param {string} taskId
   * @returns {string} existing or newly generated UUID
   */
  static ensure(taskId) {
    const key = this.#key(taskId);
    let id;
    try {
      id = sessionStorage.getItem(key);
    } catch {
      id = null;
    }
    if (!id) {
      id = generateUuid();
      try {
        sessionStorage.setItem(key, id);
      } catch {
        // Ignore storage errors (quota or privacy mode).
      }
    }
    return id;
  }

  /**
   * Retrieve the CTA instance ID for a task without generating one.
   * @param {string} taskId
   * @returns {string|null}
   */
  static get(taskId) {
    try {
      return sessionStorage.getItem(this.#key(taskId));
    } catch {
      return null;
    }
  }

  /**
   * Remove the cached CTA instance ID for a task.
   * @param {string} taskId
   * @returns {void}
   */
  static clear(taskId) {
    try {
      sessionStorage.removeItem(this.#key(taskId));
    } catch {
      // Ignore storage errors (quota or privacy mode).
    }
  }

  /**
   * Build the storage key for a task.
   * @param {string} taskId
   * @returns {string}
   */
  static #key(taskId) {
    return `ctaInstance:${taskId}`;
  }
}
